/**
 * Gedeelde iconen, allemaal op hetzelfde stramien: viewBox 0 0 24 24,
 * stroke="currentColor", strokeWidth 2 (Feather-stijl). Eén module in plaats
 * van losse SVG's per component — er stonden al twee verschillende paperclips
 * in de codebase, en dat soort drift zie je pas als het ergens vloekt.
 *
 * Geen icoonbibliotheek als dependency: dit zijn 22 paden, en currentColor
 * laat ze vanzelf meekleuren met de knop waarin ze staan.
 */

import type { SpecialUse } from "../lib/types.js";

interface IconProps {
  size?: number;
  className?: string;
}

function base(props: IconProps): {
  width: number;
  height: number;
  viewBox: string;
  fill: string;
  stroke: string;
  strokeWidth: number;
  strokeLinecap: "round";
  strokeLinejoin: "round";
  "aria-hidden": true;
  focusable: false;
  className?: string;
} {
  return {
    width: props.size ?? 16,
    height: props.size ?? 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: false,
    ...(props.className !== undefined ? { className: props.className } : {}),
  };
}

export function IconMenu(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

export function IconPen(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

export function IconReply(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="m9 17-5-5 5-5" />
      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </svg>
  );
}

export function IconReplyAll(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="m7 17-5-5 5-5" />
      <path d="m12 17-5-5 5-5" />
      <path d="M22 18v-2a4 4 0 0 0-4-4H7" />
    </svg>
  );
}

export function IconForward(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="m15 17 5-5-5-5" />
      <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
    </svg>
  );
}

export function IconStar(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01Z" />
    </svg>
  );
}

export function IconStarFilled(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)} fill="currentColor">
      <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01Z" />
    </svg>
  );
}

export function IconTrash(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function IconFolder(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
    </svg>
  );
}

export function IconInbox(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
    </svg>
  );
}

export function IconSent(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22l-4-9-9-4Z" />
    </svg>
  );
}

export function IconDrafts(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <path d="M9 15h6M9 11h2" />
    </svg>
  );
}

export function IconJunk(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="10" />
      <path d="m4.9 4.9 14.2 14.2" />
    </svg>
  );
}

export function IconArchive(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <rect x="2" y="3" width="20" height="5" rx="1" />
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
      <path d="M10 12h4" />
    </svg>
  );
}

export function IconSearch(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

export function IconPerson(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function IconPhone(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <path d="M12 18h.01" />
    </svg>
  );
}

export function IconSettings(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  );
}

export function IconLogout(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

export function IconX(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function IconPaperclip(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

export function IconChevronLeft(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

export function IconFolderPlus(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="M12 11v5M9.5 13.5h5" />
    </svg>
  );
}

/** Kebab: het menu met mapacties hangt hieronder. */
export function IconDots(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="5" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="19" r="1" fill="currentColor" />
    </svg>
  );
}

export function IconPencil(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M4 20h4L19 9a2.83 2.83 0 0 0-4-4L4 16Z" />
      <path d="m14 6 4 4" />
    </svg>
  );
}

/** Geopende envelop: "markeer als gelezen". */
export function IconMailOpen(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M3 10.5 12 4l9 6.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="m3 10.5 9 6 9-6" />
    </svg>
  );
}

/** Pijl de map in: verplaatsen. */
export function IconArrowRight(p: IconProps = {}): React.JSX.Element {
  return (
    <svg {...base(p)}>
      <path d="M4 12h15" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

/**
 * Icoon per soort map, zoals Outlook. currentColor kleurt vanzelf mee.
 * Stond eerder in FolderList; de mappenkiezer heeft hem ook nodig.
 */
export function iconForSpecialUse(specialUse: SpecialUse): React.JSX.Element {
  switch (specialUse) {
    case "inbox": return <IconInbox />;
    case "sent": return <IconSent />;
    case "drafts": return <IconDrafts />;
    case "trash": return <IconTrash />;
    case "junk": return <IconJunk />;
    case "archive": return <IconArchive />;
    default: return <IconFolder />;
  }
}
